import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Layers, Trash2, Plus, Wand2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Permission Presets manager. Reusable templates (decoupled from roles) that an
 * admin can apply to a role's matrix, create from a role's current grants, or
 * delete. Used as a starting point when creating users/roles.
 */
export default function PresetsDialog({ catalog, matrix, onApplied }) {
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyPreset, setApplyPreset] = useState("");
  const [applyRole, setApplyRole] = useState("");
  const [busy, setBusy] = useState(false);
  // create-from-role
  const [newName, setNewName] = useState("");
  const [sourceRole, setSourceRole] = useState("");

  const roles = (catalog?.roles || []).filter((r) => r !== "super_admin");

  const load = () => {
    setLoading(true);
    api.get("/permission-presets")
      .then((r) => setPresets(r.data || []))
      .catch(() => setPresets([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const doApply = async () => {
    if (!applyPreset || !applyRole) { toast.error("Pick a preset and a role."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/permission-presets/${applyPreset}/apply-to-role/${applyRole}`);
      toast.success(`Applied preset to ${catalog.role_labels[applyRole] || applyRole} (${data.granted} permissions)`);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setBusy(false); }
  };

  const doCreate = async () => {
    if (!newName.trim() || !sourceRole) { toast.error("Enter a name and source role."); return; }
    const keys = Object.entries(matrix?.[sourceRole] || {}).filter(([, v]) => v).map(([k]) => k);
    setBusy(true);
    try {
      await api.post("/permission-presets", {
        name: newName.trim(),
        description: `Created from ${catalog.role_labels[sourceRole] || sourceRole} grants`,
        keys,
      });
      toast.success(`Preset "${newName.trim()}" created (${keys.length} permissions)`);
      setNewName(""); setSourceRole("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/permission-presets/${id}`);
      toast.success("Preset deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-9" data-testid="perm-presets-btn">
          <Layers className="w-3.5 h-3.5 mr-1.5" /> Presets
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="presets-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-500" /> Permission Presets
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Apply preset to role */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
              <Wand2 className="w-3.5 h-3.5" /> Apply preset to a role
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={applyPreset} onValueChange={setApplyPreset}>
                <SelectTrigger className="h-9 flex-1 min-w-[160px]" data-testid="apply-preset-select">
                  <SelectValue placeholder="Choose preset" />
                </SelectTrigger>
                <SelectContent>
                  {presets.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.keys.length})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={applyRole} onValueChange={setApplyRole}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="apply-role-select">
                  <SelectValue placeholder="To role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r} value={r}>{catalog.role_labels[r] || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" className="h-9 bg-indigo-600 hover:bg-indigo-700" onClick={doApply}
                disabled={busy} data-testid="apply-preset-btn">Apply</Button>
            </div>
            <p className="text-[11px] text-slate-400">Overwrites the role's matrix column with the preset's grants.</p>
          </div>

          {/* Create preset from a role */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Create preset from a role's grants
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="Preset name (e.g. Phone Agent)" className="h-9 flex-1 min-w-[160px]"
                data-testid="new-preset-name" />
              <Select value={sourceRole} onValueChange={setSourceRole}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="new-preset-source">
                  <SelectValue placeholder="From role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r} value={r}>{catalog.role_labels[r] || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="h-9" onClick={doCreate}
                disabled={busy} data-testid="create-preset-btn">Create</Button>
            </div>
          </div>

          {/* Preset list */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              All presets {loading ? "" : `(${presets.length})`}
            </div>
            <ul className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="presets-list">
              {presets.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                      {p.name}
                      {p.is_system && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">system</Badge>}
                    </div>
                    <div className="text-[11px] text-slate-400 truncate">{p.description} · {p.keys.length} permissions</div>
                  </div>
                  {!p.is_system && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:text-rose-700"
                      onClick={() => doDelete(p.id)} data-testid={`delete-preset-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
